
import { supabase } from '@/integrations/supabase/client';

export class UserManager {
  private currentUser: string | null = null;
  private anonymousUserId: string | null = null;
  private hasAnalyticsConsent: boolean = false;
  private hasATTConsent: boolean = false;
  private readonly ANONYMOUS_USER_KEY = 'forkast_anonymous_user_id';

  setAnalyticsConsent(hasConsent: boolean) {
    this.hasAnalyticsConsent = hasConsent;
  }

  getAnalyticsConsent(): boolean {
    return this.hasAnalyticsConsent;
  }

  setATTConsent(hasConsent: boolean) {
    this.hasATTConsent = hasConsent;
  }

  getATTConsent(): boolean {
    return this.hasATTConsent;
  }

  canTrack(): boolean {
    return this.hasAnalyticsConsent && this.hasATTConsent;
  }

  setUser(userId: string | null) {
    this.currentUser = userId;
    
    // If user logs in and we had an anonymous ID, we should alias them
    if (userId && this.anonymousUserId && userId !== this.anonymousUserId) {
      // Aliasing will be handled by the AnalyticsService
    }
  }

  private generateAnonymousUserId(): string {
    // Try to get existing anonymous ID from localStorage
    try {
      const existingId = localStorage.getItem(this.ANONYMOUS_USER_KEY);
      if (existingId) {
        return existingId;
      }
    } catch (error) {
      console.warn('Could not access localStorage for anonymous user ID');
    }

    // Generate new anonymous ID
    const anonymousId = crypto.randomUUID();
    
    // Try to save it to localStorage
    try {
      localStorage.setItem(this.ANONYMOUS_USER_KEY, anonymousId);
    } catch (error) {
      console.warn('Could not save anonymous user ID to localStorage');
    }
    
    return anonymousId;
  }

  async getCurrentUser(): Promise<string | null> {
    // First try to get authenticated user
    if (this.currentUser) return this.currentUser;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id) {
        this.currentUser = user.id;
        return this.currentUser;
      }
    } catch (error) {
      console.error('Error getting authenticated user for analytics:', error);
    }
    
    // If no authenticated user, use anonymous ID
    if (!this.anonymousUserId) {
      this.anonymousUserId = this.generateAnonymousUserId();
    }
    
    return this.anonymousUserId;
  }

  getAnonymousUserId(): string | null {
    return this.anonymousUserId;
  }

  clearAnonymousUserId() {
    this.anonymousUserId = null;
    try {
      localStorage.removeItem(this.ANONYMOUS_USER_KEY);
    } catch (error) {
      console.warn('Could not clear anonymous user ID from localStorage');
    }
  }
}
